/**
 * AdsManagerTab - single-page entity tree view of the campaign's live publisher state.
 *
 * Publisher-aware: Google renders CAMPAIGN -> AD_GROUP -> AD -> ASSET; ADSP renders the
 * CCG tree PRODUCT_SET -> CONCEPT -> CREATIVE -> ASSET; Meta and TikTok render their catalog
 * PRODUCT_SET -> product (ASSET) view. The Google path is unchanged.
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { Loader } from 'src/components/common/Loader';
import type { AppDispatch } from 'src/store';
import type { Publisher } from 'src/store/generated/leela-api-generated';
import { CampaignPublishers } from 'src/store/constants';
import {
  leelaApi,
  usePublisherEntitiesControllerListForCampaignQuery,
} from 'src/store/leela-api';
import { EntitiesView } from './adsManager/EntitiesView';
import { AdspEntitiesView } from './adsManager/AdspEntitiesView';
import { CatalogEntitiesView } from './adsManager/CatalogEntitiesView';

interface AdsManagerTabProps {
  campaignId: string;
  publisher: Publisher;
}

export const AdsManagerTab: React.FC<AdsManagerTabProps> = ({ campaignId, publisher }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isAdsp = publisher === CampaignPublishers.ADSP;
  const isCatalog = publisher === CampaignPublishers.META || publisher === CampaignPublishers.TIKTOK;

  // Google entity queries (skipped for ADSP/Meta/TikTok - no CAMPAIGN/AD_GROUP entities exist).
  const campaignQuery = usePublisherEntitiesControllerListForCampaignQuery({
    campaignId, pageSize: 1, publisher: 'google', publisherEntityType: 'CAMPAIGN',
  }, { skip: isAdsp || isCatalog });
  const adGroupsQuery = usePublisherEntitiesControllerListForCampaignQuery({
    campaignId, pageSize: 100, publisher: 'google', publisherEntityType: 'AD_GROUP',
  }, { skip: isAdsp || isCatalog });

  const handleRefresh = () => {
    dispatch(leelaApi.util.invalidateTags(['PublisherEntities']));
  };

  // ADSP renders the CCG tree PRODUCT_SET -> CONCEPT -> CREATIVE -> ASSET. The view
  // owns its own server-filtered, lazy-loaded, paginated queries per level.
  if (isAdsp) {
    return (
      <AdspEntitiesView campaignId={campaignId} onRefresh={handleRefresh} />
    );
  }

  // Meta/TikTok render one card per catalog Product Set with its paginated products.
  if (isCatalog) {
    return (
      <CatalogEntitiesView campaignId={campaignId} publisher={publisher} onRefresh={handleRefresh} />
    );
  }

  const isLoading = campaignQuery.isLoading || adGroupsQuery.isLoading;
  const isFetching = campaignQuery.isFetching || adGroupsQuery.isFetching;

  return (
    <Loader isLoading={isLoading}>
      <EntitiesView
        adGroupsQuery={adGroupsQuery}
        campaignId={campaignId}
        campaignQuery={campaignQuery}
        isFetching={isFetching}
        onRefresh={handleRefresh}
      />
    </Loader>
  );
};
